import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Gift, Copy, Check, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { portalApi } from "@/lib/portalApi";
import { track } from "@/lib/analytics";

const STATUS_LABEL = {
  signed_up: { label: "Signed up", color: "#94A3B8" },
  qualified: { label: "Qualified", color: "#FACC15" },
  rewarded: { label: "Month earned", color: "#4ADE80" },
};

const Stat = ({ value, label, testid }) => (
  <div className="border border-slate-800 bg-[#0B1220]/70 p-5" data-testid={testid}>
    <p className="font-serif text-3xl text-white leading-none">{value}</p>
    <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-2">{label}</p>
  </div>
);

export default function ReferPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    portalApi
      .referrals()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  const link = data ? `${window.location.origin}${data.share_path}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      track("referral_link_copied");
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed — select the link and copy it manually");
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex justify-center py-24" data-testid="refer-loading">
          <Loader2 className="animate-spin text-[#D4AF37]" size={26} />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div data-testid="refer-page">
        <div className="mb-10 max-w-2xl">
          <Gift className="text-[#D4AF37] mb-5" size={30} />
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] mb-3">Refer a colleague</p>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Give them a way out — get a free month
          </h1>
          <p className="text-slate-400 text-sm leading-relaxed">
            Every person who joins through your link and becomes a paying member — by upgrading to Premium
            or booking a session — earns you{" "}
            <span className="text-white">one month of Premium, free</span> — up to {data.months_remaining + data.qualified}{" "}
            months. And they start with <span className="text-white">50% off their first month</span> (or 10% off their
            first year), so there is nothing for them to lose.
          </p>
        </div>

        {data.bonus_premium_until && (
          <div
            className="border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] p-6 mb-8 flex items-center gap-4"
            data-testid="refer-bonus-active"
          >
            <Sparkles className="text-[#D4AF37] shrink-0" size={18} />
            <p className="text-slate-300 text-sm">
              Premium is unlocked on your account for the next{" "}
              <span className="text-white">{data.bonus_days_left} days</span> — earned through referrals.
            </p>
          </div>
        )}

        <div className="border border-slate-800 bg-[#0B1220]/70 p-6 md:p-8 mb-10" data-testid="refer-link-panel">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">Your invite link</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              readOnly
              value={link}
              data-testid="refer-link-input"
              className="flex-1 bg-slate-900 border border-slate-800 px-4 py-3 text-slate-300 text-sm font-mono"
            />
            <button
              onClick={copy}
              data-testid="refer-copy-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-slate-950 px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          <p className="text-slate-500 text-sm mt-4 leading-relaxed">
            Any redacted report you share also carries your code, so a colleague who signs up from it counts
            automatically.{" "}
            <Link to="/portal/report" className="text-[#D4AF37] hover:underline" data-testid="refer-to-report-link">
              Share a report →
            </Link>
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Stat value={data.signups} label="Signed up" testid="refer-stat-signups" />
          <Stat value={data.qualified} label="Became paying" testid="refer-stat-qualified" />
          <Stat value={data.months_earned} label="Free months earned" testid="refer-stat-months" />
          <Stat value={data.months_remaining} label="Months still available" testid="refer-stat-remaining" />
        </div>

        <div>
          <h2 className="font-serif text-xl md:text-2xl text-white mb-6">Your referrals</h2>
          {!data.referrals.length ? (
            <div className="border border-dashed border-slate-800 p-10 text-center" data-testid="refer-empty">
              <Users className="text-slate-700 mx-auto mb-4" size={24} />
              <p className="text-slate-500 text-sm">
                Nobody yet. Most people share it with one colleague who is going through the same thing.
              </p>
            </div>
          ) : (
            <div className="border border-slate-800 bg-[#0B1220]/70 divide-y divide-slate-800" data-testid="refer-list">
              {data.referrals.map((r, idx) => {
                const meta = STATUS_LABEL[r.status] || STATUS_LABEL.signed_up;
                return (
                  <div key={idx} className="flex items-center justify-between gap-4 p-5" data-testid={`refer-row-${idx}`}>
                    <div>
                      <p className="text-white text-sm">{r.referee_name}</p>
                      <p className="text-slate-500 text-xs mt-1">
                        {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </div>
                    <div className="flex items-center gap-5">
                      {r.reward_months > 0 && (
                        <span className="text-[#D4AF37] text-xs font-mono">+{r.reward_months} month</span>
                      )}
                      <span className="text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>
                        {meta.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
